import { useCallback, useMemo } from "react";
import { EnumProductType } from "gql/generated";
import { getMultilingualString } from "shared/utils";
import { useBookerContext } from "../BookerContextProvider";
import { useCartContext } from "./useCartContext";
import {
  buildInitialGuestsPerTierForProduct,
  convertCartToProductPurchaseRequests,
} from "./helpers";
import { CartSnapshot } from "./cartSnapshotTypes";

export const useCartPersistence = () => {
  const cartContext = useCartContext();
  const { product, language } = useBookerContext();

  const serializeCart = useMemo(
    () => (): CartSnapshot => {
      const draft = cartContext.getInvoiceAccommodationDraft();
      const draftForProduct =
        draft && product?._id && draft.productId === product._id ? draft : null;

      let cartForSnapshot = cartContext.cart.map(item => {
        const base = {
          ...item,
          startDateISO: item.startDateISO ?? "",
          endDateISO: item.endDateISO ?? "",
        };
        if (!draftForProduct || item.productId !== draftForProduct.productId) {
          return base;
        }
        return {
          ...base,
          startDateISO: draftForProduct.startDateISO || base.startDateISO,
          endDateISO: draftForProduct.endDateISO || base.endDateISO,
          guestsPerTier:
            draftForProduct.guestsPerTier.length > 0
              ? draftForProduct.guestsPerTier
              : base.guestsPerTier,
        };
      });

      if (
        cartForSnapshot.length === 0 &&
        product?._id &&
        product.type !== EnumProductType.Event &&
        product.type !== EnumProductType.Merch
      ) {
        const guestsPerTier =
          draftForProduct && draftForProduct.guestsPerTier.length > 0
            ? draftForProduct.guestsPerTier
            : buildInitialGuestsPerTierForProduct(product);
        cartForSnapshot = [
          {
            productId: product._id,
            productName: getMultilingualString(product.name, language) ?? "",
            productType: product.type,
            startDateISO: draftForProduct?.startDateISO ?? "",
            endDateISO: draftForProduct?.endDateISO ?? "",
            total: 0,
            guestsPerTier,
            quantity: 1,
          },
        ];
      }

      return {
        cart: cartForSnapshot,
        productPurchaseRequests: convertCartToProductPurchaseRequests(
          cartForSnapshot,
          product?.pricing,
          cartContext.selectedTierId,
        ),
        selectedBillingInterval: cartContext.selectedBillingInterval,
        selectedTierId: cartContext.selectedTierId,
        selectedOperatorId: cartContext.selectedOperatorId,
        businessField: cartContext.businessField,
        guestNames: draftForProduct
          ? draftForProduct.guestNames
          : cartContext.guestNames,
        guests: cartContext.eventGuestDetails
          .filter(g => g.name.trim().length > 0)
          .map(g => ({
            name: g.name.trim(),
            ...(g.email.trim() ? { email: g.email.trim() } : {}),
          })),
        promoCode: cartContext.promoCode,
        customer: {
          firstName: cartContext.customer.firstName,
          lastName: cartContext.customer.lastName,
          email: cartContext.customer.email,
          phone: cartContext.customer.phone,
          complete: cartContext.customer.complete,
        },
        customerComplete: cartContext.customer.complete,
        questionnaireResponses: cartContext.questionnaireResponses,
        questionnaireComplete: cartContext.questionnaireComplete,
        acceptedPolicies: cartContext.acceptedPolicies,
        customerAddress: cartContext.customerAddress ?? undefined,
        taxExempt: cartContext.taxExempt,
        taxExemptionNumber: cartContext.taxExemptionNumber,
      };
    },
    [cartContext, language, product?.pricing],
  );

  const restoreCart = useCallback(
    (snapshot: CartSnapshot) => {
      cartContext.hydrateSnapshot(snapshot);
    },
    [cartContext.hydrateSnapshot],
  );

  return {
    serializeCart,
    restoreCart,
  };
};
